import { apiClient } from './apiClient';
import { 
  Budget, 
  CreateBudgetRequest, 
  UpdateBudgetRequest,
  PaginatedResponse
} from './types';

export interface GetBudgetsParams {
  search?: string;
  isActive?: boolean;
  pageNumber?: number;
  pageSize?: number;
}

export interface BudgetTemplateCategorySuggestion {
  categoryId: string;
  categoryName: string;
  probability: number;
  suggestedAmount: number;
  occurrenceCount: number;
}

export interface BudgetTemplateSuggestion {
  suggestedName: string;
  startDate: string;
  endDate: string;
  weekOfMonth: number;
  categories: BudgetTemplateCategorySuggestion[];
  historicalBudgetsCount: number;
  confidenceLevel: 'low' | 'medium' | 'high';
}

export const budgetService = {
  async getAll(): Promise<Budget[]> {
    return apiClient.get<Budget[]>('/budgets');
  },

  async getAllPaged(params: GetBudgetsParams = {}): Promise<PaginatedResponse<Budget>> {
    const queryParams = new URLSearchParams();
    
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.isActive !== undefined) {
      queryParams.append('isActive', params.isActive.toString());
    }
    if (params.pageNumber !== undefined) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize !== undefined) {
      queryParams.append('pageSize', params.pageSize.toString());
    }

    const queryString = queryParams.toString();
    return apiClient.get<PaginatedResponse<Budget>>(`/budgets${queryString ? `?${queryString}` : ''}`);
  },

  async getById(id: string): Promise<Budget> {
    return apiClient.get<Budget>(`/budgets/${id}`);
  },

  async create(budget: CreateBudgetRequest): Promise<Budget> {
    return apiClient.post<CreateBudgetRequest, Budget>('/budgets', budget);
  },

  async update(id: string, budget: UpdateBudgetRequest): Promise<Budget> {
    return apiClient.put<UpdateBudgetRequest, Budget>(`/budgets/${id}`, budget);
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/budgets/${id}`);
  },

  async toggleActive(id: string): Promise<Budget> {
    return apiClient.post<{}, Budget>(`/budgets/${id}/toggle-active`, {});
  },

  async getWeeklyTemplateSuggestion(targetDate: string): Promise<BudgetTemplateSuggestion> {
    return apiClient.get<BudgetTemplateSuggestion>(
      `/budgets/template-suggestion/weekly?targetDate=${encodeURIComponent(targetDate)}`
    );
  }
};
